import { PropsWithChildren } from 'react'
import { z } from 'zod'
import { useNotificationTemplateOptions } from 'app/frontend/base/config/queries'
import { notificationCtaTargets } from 'app/frontend/types/custom/NotificationTemplateCtaTarget'
import { notificationDeliveryMethods } from 'app/frontend/types/custom/NotificationTemplateMethod'
import { notificationDeliveryTargets } from 'app/frontend/types/custom/NotificationTemplateTarget'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import NotificationFormContent from './NotificationFormContent'
import { cronErrors } from 'app/frontend/base/components/form-fields/CronField'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'

const scheduleSchemaOptions = {
  cronSchedule: z.string().superRefine((data, context) => {
    const error = cronErrors(data, 8)
    if (error) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: error,
      })
    }
  }),
}

const cooldownSchemaOptions = {
  cooldown: z.number().int().min(1),
}

const discordChannelId = /^\d{17,19}$/
const discordWebhookUrl =
  /^https:\/\/(canary\.|ptb\.)?discord(app)?\.com\/api\/(v\d+\/)?webhooks\/\d+\/[\w-]+/i

const formSchema = z
  .object({
    type: z.string(),
    delayInMinutes: z.preprocess(
      (value) => (value === null || value === '' ? 0 : value),
      z.number().int().min(0).max(720),
    ),
    messageTitle: z.string().optional().or(z.null()),
    messageTemplate: z.string(),
    ctaLabel: z.string().optional(),
    ctaTarget: z.enum(notificationCtaTargets),
    customCtaUrl: z.string().url().optional().or(z.null()),
    deliveryMethod: z.enum(notificationDeliveryMethods),
    deliveryTargetId: z.number().int().optional().or(z.null()),
    teamRankId: z.number().int().optional().or(z.null()),
    targetAddress: z
      .string()
      .url()
      .optional()
      .or(z.null())
      .or(z.string().regex(discordChannelId, 'Invalid Discord ID')),
    criteria: z.union([
      z.array(
        z.record(
          z.union([
            z.string(),
            z
              .string()
              .or(z.number())
              .or(z.null())
              .or(z.array(z.string().or(z.number())))
              .optional(),
          ]),
        ),
      ),
      z.array(z.string()),
    ]),
  })
  .and(
    z.discriminatedUnion('type', [
      z.object({
        type: z.literal('Template::CharacterGear'),
        ...scheduleSchemaOptions,
        deliveryTarget: z.enum(notificationDeliveryTargets),
      }),
      z.object({
        type: z.literal('Template::CharacterVaultOptions'),
        ...scheduleSchemaOptions,
        deliveryTarget: z.enum(notificationDeliveryTargets),
      }),
      z.object({
        type: z.literal('Template::CharacterLeftGuild'),
        ...cooldownSchemaOptions,
        deliveryTarget: z.enum(notificationDeliveryTargets),
      }),
      z.object({
        type: z.literal('Template::CharacterMissing'),
        ...scheduleSchemaOptions,
        deliveryTarget: z.enum(notificationDeliveryTargets),
      }),
      z.object({
        type: z.literal('Template::CharacterUnlinked'),
        ...scheduleSchemaOptions,
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        type: z.literal('Template::CharacterUpdate'),
        deliveryTarget: z.enum(notificationDeliveryTargets),
      }),
      z.object({
        type: z.literal('Template::EventSignupChange'),
        deliveryTarget: z.enum(notificationDeliveryTargets),
      }),
      z.object({
        type: z.literal('Template::EventSignupSummary'),
        ...scheduleSchemaOptions,
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        type: z.literal('Template::EventMissingSignup'),
        deliveryTarget: z.enum(notificationDeliveryTargets),
      }),
      z.object({
        type: z.literal('Template::EventIndividualOverview'),
        deliveryTarget: z.enum(['user', 'subject']),
      }),
      z.object({
        type: z.literal('Template::EventInteractiveOverview'),
        deliveryTarget: z.enum(['team', 'user']),
        deliveryMethod: z.enum(['discord_message']),
      }),
      z.object({
        type: z.literal('Template::EventUpdate'),
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        type: z.literal('Template::NewsGlobalPublished'),
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        type: z.literal('Template::NewsUpdate'),
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        type: z.literal('Template::TeamUpdate'),
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        ...scheduleSchemaOptions,
        ...cooldownSchemaOptions,
        type: z.literal('Template::NotificationWarning'),
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        ...scheduleSchemaOptions,
        type: z.literal('Template::RecruitmentSummary'),
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        type: z.literal('Template::RecruitmentUpdate'),
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        type: z.literal('Template::WishlistMissing'),
        ...scheduleSchemaOptions,
        deliveryTarget: z.enum(['subject', 'user']),
      }),
      z.object({
        type: z.literal('Template::WishlistMissingSummary'),
        ...scheduleSchemaOptions,
        deliveryTarget: z.enum(['team', 'user']),
      }),
      z.object({
        type: z.literal('Template::WishlistUpdate'),
        deliveryTarget: z.enum(notificationDeliveryTargets),
      }),
    ]),
  )
  .refine(
    (data) => data.deliveryMethod !== 'email' || data.messageTitle?.length,
    {
      message: 'Required',
      path: ['messageTitle'],
    },
  )
  .refine(
    (data) => data.deliveryMethod !== 'email' || data.deliveryTarget !== 'team',
    {
      message: 'Emails cannot be sent to teams',
      path: ['deliveryTarget'],
    },
  )
  .refine((data) => data.deliveryTarget !== 'user' || data.deliveryTargetId, {
    message: 'Required',
    path: ['deliveryTargetId'],
  })
  .refine(
    (data) =>
      data.targetAddress ||
      data.deliveryMethod === 'email' ||
      data.deliveryTarget === 'user' ||
      data.type === 'Template::EventInteractiveOverview' ||
      (data.deliveryMethod === 'discord_message' &&
        data.deliveryTarget !== 'team'),
    {
      message: 'Required',
      path: ['targetAddress'],
    },
  )
  .refine((data) => data.ctaTarget !== 'custom_url' || data.customCtaUrl, {
    message: 'Required',
    path: ['customCtaUrl'],
  })
  .refine(
    (data) =>
      data.deliveryMethod !== 'discord_message' ||
      !data.targetAddress ||
      discordChannelId.test(data.targetAddress) ||
      discordWebhookUrl.test(data.targetAddress),
    {
      message: 'Must be a valid Discord webhook URL',
      path: ['targetAddress'],
    },
  )
  .refine(
    (data) =>
      data.deliveryMethod !== 'custom_webhook' ||
      (!!data.targetAddress &&
        z.string().url().safeParse(data.targetAddress).success),
    {
      message: 'Must be a valid URL',
      path: ['targetAddress'],
    },
  )

interface Props {
  template: Partial<wowaudit.NotificationTemplate>
  setTemplate: (value?: Partial<wowaudit.NotificationTemplate>) => void
  backLabel?: string
}

const NotificationForm = ({
  template,
  setTemplate,
  backLabel,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { data: typeOptions } = useNotificationTemplateOptions()
  const queryClient = useQueryClient()
  if (!typeOptions) return null

  return (
    <>
      <FormWrapper<Partial<wowaudit.NotificationTemplate>>
        entityType='notification_template'
        parentEntityType='team'
        parentId={team.id}
        formSchema={formSchema}
        onSave={(form, data) => {
          form.reset(data)
          queryClient.invalidateQueries({
            queryKey: ['notificationTemplates', { id: team.id }],
          })
          setTemplate(undefined)
        }}
        allowDelete={true}
        extraActions={[
          <Button
            key='back-to-list'
            size='sm'
            variant='secondary'
            type='button'
            onClick={() => setTemplate(undefined)}
          >
            {backLabel ?? t('shared.back-to-list')}
          </Button>,
        ]}
        entity={template}
        stringifyValues={['criteria']}
        i18nPrefix='notifications.form-fields'
      >
        <NotificationFormContent typeOptions={typeOptions} />
      </FormWrapper>
    </>
  )
}

export default NotificationForm
